import React, { useState, useEffect } from 'react';
import {
  Camera, CreditCard, Cloud, MessageSquare, Settings2, Zap, Mail,
  ChevronDown, ChevronUp, Check, AlertCircle, Loader2, RefreshCw,
  Eye, EyeOff, Wifi, WifiOff,
} from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { Input } from '@/core/ui/components/Input';
import { useIntegrationCatalog } from '../hooks/useIntegrationCatalog';
import { SyncDashboard } from './SyncDashboard';
import { AccessLevelSelector } from './AccessLevelSelector';
import { HikCentralVisitorGroupConfig } from './HikCentralVisitorGroupConfig';
import type { CatalogItem, CatalogGroup, ConfigField } from '@yawi/types';

const ICON_MAP: Record<string, React.ReactNode> = {
  camera: <Camera size={18} className="text-blue-600" />,
  'credit-card': <CreditCard size={18} className="text-violet-600" />,
  cloud: <Cloud size={18} className="text-amber-600" />,
  mail: <Mail size={18} className="text-red-600" />,
  'message-square': <MessageSquare size={18} className="text-green-600" />,
  settings: <Settings2 size={18} className="text-purple-600" />,
  zap: <Zap size={18} className="text-sky-600" />,
};

const ICON_BG_MAP: Record<string, string> = {
  camera: 'bg-blue-50',
  'credit-card': 'bg-violet-50',
  cloud: 'bg-amber-50',
  mail: 'bg-red-50',
  'message-square': 'bg-green-50',
  settings: 'bg-purple-50',
  zap: 'bg-sky-50',
};

function getIcon(iconName: string): React.ReactNode {
  return ICON_MAP[iconName] ?? <Settings2 size={18} className="text-slate-600" />;
}

function getIconBg(iconName: string): string {
  return ICON_BG_MAP[iconName] ?? 'bg-slate-50';
}

// ============ Dynamic Field Renderer ============
interface FieldRendererProps {
  field: ConfigField;
  value: unknown;
  disabled?: boolean;
  onChange: (key: string, value: unknown) => void;
}

const FieldRenderer: React.FC<FieldRendererProps> = ({ field, value, disabled, onChange }) => {
  const [showPassword, setShowPassword] = useState(false);

  if (field.type === 'boolean') {
    const checked = value === true || value === 'true';
    return (
      <div className="flex items-center justify-between py-2">
        <div>
          <label className="text-sm font-medium text-slate-700">{field.label}</label>
          {field.helpText && <p className="text-xs text-slate-400 mt-0.5">{field.helpText}</p>}
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(field.key, !checked)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            checked ? 'bg-green-500' : 'bg-slate-200'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
    );
  }

  if (field.type === 'select' && field.options) {
    return (
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">{field.label}</label>
        <select
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(field.key, e.target.value)}
          className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="">{field.placeholder ?? 'Select...'}</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        {field.helpText && <p className="text-xs text-slate-400 mt-1">{field.helpText}</p>}
      </div>
    );
  }

  const isPasswordType = field.type === 'password' || field.sensitive;

  return (
    <div className="relative">
      <Input
        label={field.label}
        type={isPasswordType && !showPassword ? 'password' : (field.type === 'password' ? 'text' : field.type)}
        placeholder={field.placeholder}
        value={String(value ?? '')}
        disabled={disabled}
        onChange={(e) => onChange(field.key, field.type === 'number' ? Number(e.target.value) : e.target.value)}
      />
      {isPasswordType && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[34px] text-slate-400 hover:text-slate-600"
        >
          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      )}
      {field.helpText && <p className="text-xs text-slate-400 mt-1">{field.helpText}</p>}
    </div>
  );
};

// ============ Integration Card ============
interface IntegrationItemCardProps {
  item: CatalogItem;
  category: string;
  canUpdate: boolean;
  onToggle: (provider: string, enabled: boolean, integrationId?: string) => Promise<boolean>;
  onSave: (provider: string, config: Record<string, unknown>, enabled?: boolean) => Promise<boolean>;
  onTest: (provider: string) => Promise<unknown>;
  onLoadConfig: (provider: string) => Promise<{ config: Record<string, unknown> } | null>;
}

const IntegrationItemCard: React.FC<IntegrationItemCardProps> = ({
  item, category, canUpdate, onToggle, onSave, onTest, onLoadConfig
}) => {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [configLoaded, setConfigLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleExpand = async () => {
    const shouldExpand = !expanded;
    setExpanded(shouldExpand);

    if (shouldExpand && !configLoaded) {
      const config = await onLoadConfig(item.provider);
      if (config?.config) {
        setFormValues(config.config);
      }
      setConfigLoaded(true);
    }
  };

  const handleFieldChange = (key: string, value: unknown) => {
    if (!canUpdate) return;
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!canUpdate) return;
    setSaving(true);
    await onSave(item.provider, formValues, item.isEnabled);
    setSaving(false);
  };

  const handleTest = async () => {
    if (!canUpdate) return;
    setTesting(true);
    await onTest(item.provider);
    setTesting(false);
  };

  const handleToggle = async () => {
    if (!canUpdate) return;
    setToggling(true);
    await onToggle(item.provider, !item.isEnabled, item.integrationId);
    setToggling(false);
  };

  const hasOptionalFields = item.optionalFields.length > 0;

  return (
    <div className={`bg-white rounded-xl border transition-all ${
      item.isEnabled ? 'border-green-200 shadow-sm' : 'border-slate-100'
    }`}>
      {/* Header */}
      <div
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={handleExpand}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${getIconBg(item.icon)}`}>
            {getIcon(item.icon)}
          </div>
          <div>
            <h5 className="font-semibold text-slate-800 flex items-center gap-2">
              {item.displayName}
              {item.isConfigured && item.isEnabled && <Check size={14} className="text-green-500" />}
            </h5>
            <p className="text-sm text-slate-500">{item.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Status badge */}
          {item.isConfigured ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 hidden sm:inline-flex items-center gap-1">
              <Wifi size={10} />
              {t('admin.settings.integrations.configured')}
            </span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 hidden sm:inline-flex items-center gap-1">
              <WifiOff size={10} />
              {t('admin.settings.integrations.notConfigured')}
            </span>
          )}

          {/* Error count */}
          {item.errorCount != null && item.errorCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 hidden sm:inline-flex items-center gap-1">
              <AlertCircle size={10} />
              {item.errorCount} {t('admin.settings.integrations.errors')}
            </span>
          )}

          {/* Toggle */}
          {canUpdate && (
            <button
              type="button"
              disabled={toggling}
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.isEnabled ? 'bg-green-500' : 'bg-slate-200'
              } ${toggling ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                item.isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          )}

          {expanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </div>
      </div>

      {/* Expanded form */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-4">
          {/* Last used info */}
          {item.lastUsedAt && (
            <p className="text-xs text-slate-400 mb-3">
              {t('admin.settings.integrations.lastUsed')}: {new Date(item.lastUsedAt).toLocaleDateString()}
            </p>
          )}

          {/* Required fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {item.requiredFields.map((field) => (
              <FieldRenderer
                key={field.key}
                field={field}
                value={formValues[field.key]}
                disabled={!canUpdate}
                onChange={handleFieldChange}
              />
            ))}
          </div>

          {/* Optional fields */}
          {hasOptionalFields && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowOptional(!showOptional)}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                {showOptional
                  ? t('admin.settings.integrations.hideOptional')
                  : t('admin.settings.integrations.showOptional')
                }
                {showOptional ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showOptional && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  {item.optionalFields.map((field) => (
                    <FieldRenderer
                      key={field.key}
                      field={field}
                      value={formValues[field.key]}
                      disabled={!canUpdate}
                      onChange={handleFieldChange}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          {canUpdate && (
            <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                {saving ? t('admin.settings.integrations.saving') : t('common.save')}
              </button>

              {item.isConfigured && (
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={testing}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2"
                >
                  {testing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Wifi size={14} />
                  )}
                  {testing ? t('admin.settings.integrations.testing') : t('admin.settings.integrations.test')}
                </button>
              )}
            </div>
          )}

          {/* Sync Dashboard — only for ACCESS integrations that are configured & enabled */}
          {category === 'ACCESS_SECURITY' && item.isConfigured && item.isEnabled && (
            <SyncDashboard />
          )}
          {category === 'ACCESS_SECURITY'
            && item.provider === 'HIKCENTRAL'
            && item.isConfigured
            && item.isEnabled
            && item.integrationId && (
            <>
              <HikCentralVisitorGroupConfig integrationId={item.integrationId} />
              <AccessLevelSelector integrationId={item.integrationId} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============ Main Component ============
interface IntegrationsTabProps {
  canUpdate?: boolean;
}

export const IntegrationsTab: React.FC<IntegrationsTabProps> = ({ canUpdate = false }) => {
  const { t } = useI18n();
  const {
    catalog,
    loading,
    error,
    fetchCatalog,
    loadProviderConfig,
    saveProviderConfig,
    toggleProvider,
    testConnection,
  } = useIntegrationCatalog();

  if (loading) {
    return (
      <div className="space-y-6 animate-in fade-in">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-3">
            <div className="h-6 bg-slate-100 rounded w-48 animate-pulse" />
            {[1, 2].map((j) => (
              <div key={j} className="h-20 bg-slate-50 rounded-xl border border-slate-100 animate-pulse" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center animate-in fade-in">
        <AlertCircle size={48} className="text-red-400 mb-4" />
        <p className="text-slate-600 mb-4">{t('admin.settings.integrations.errorLoading')}</p>
        <button
          type="button"
          onClick={fetchCatalog}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 flex items-center gap-2"
        >
          <RefreshCw size={14} />
          {t('common.retry') ?? 'Retry'}
        </button>
      </div>
    );
  }

  if (!catalog || catalog.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center animate-in fade-in">
        <Settings2 size={48} className="text-slate-300 mb-4" />
        <p className="text-slate-500">No integrations available.</p>
      </div>
    );
  }

  const HIDDEN_CATEGORIES = ['CLOUD_SERVICES', 'COMMUNICATION'];

  return (
    <div className="space-y-8 animate-in fade-in">
      {catalog.filter((group) => !HIDDEN_CATEGORIES.includes(group.category)).map((group) => (
        <div key={group.category}>
          <div className="mb-4">
            <h4 className="font-bold text-slate-800 text-base">{group.displayName}</h4>
            {group.description && (
              <p className="text-sm text-slate-500 mt-0.5">{group.description}</p>
            )}
          </div>
          <div className="space-y-3">
            {group.items.map((item) => (
              <IntegrationItemCard
                key={item.provider}
                item={item}
                category={group.category}
                canUpdate={canUpdate}
                onToggle={toggleProvider}
                onSave={saveProviderConfig}
                onTest={testConnection}
                onLoadConfig={loadProviderConfig}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
